import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, CalendarCheck, Camera } from "lucide-react";
import { api } from "@/lib/api";
import { longDate, shopDateTime } from "@/lib/dates";
import { useFetch } from "@/lib/hooks";
import { AdminLayout } from "@/components/admin/AdminLayout";

const TABS = ["New", "Contacted", "Archived"];

export const MessageBadges = ({ m }) => (
  <>
    {m.email_alert === "failed" && (
      <span className="inline-flex items-center gap-1 rounded-sm bg-[#C8322B]/20 px-2 py-0.5 text-xs font-semibold text-[#ff8a80]" data-testid="email-alert-failed-badge">
        <AlertTriangle className="h-3 w-3" /> Email alert not sent
      </span>
    )}
    {m.appointment_date && (
      <span className="inline-flex items-center gap-1 rounded-sm bg-[#FFD23F]/15 px-2 py-0.5 text-xs font-semibold text-[#FFD23F]" data-testid="message-appointment-badge">
        <CalendarCheck className="h-3 w-3" /> Appointment: {longDate(m.appointment_date)}
      </span>
    )}
  </>
);

export const Tabs = ({ tabs, active, onChange, counts = {}, prefix }) => (
  <div className="mb-4 grid gap-1 rounded-lg border border-zinc-800 bg-[#1A1A1E] p-1" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }} role="tablist">
    {tabs.map(([key, label]) => (
      <button key={key} role="tab" aria-selected={active === key} onClick={() => onChange(key)}
        className={`h-12 rounded-md px-1 text-sm font-semibold transition-colors sm:text-base ${active === key ? "bg-[#FFD23F] text-[#121214]" : "text-zinc-300 hover:bg-zinc-800"}`}
        data-testid={`${prefix}-tab-${key.toLowerCase()}`}>
        {label}{counts[key] !== undefined && <span className="ml-1 opacity-70">({counts[key]})</span>}
      </button>
    ))}
  </div>
);

export default function MessagesPage() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "New";
  const { data } = useFetch(() => api.get("/admin/messages", { params: { status: tab } }), [tab]);
  return (
    <AdminLayout title="Messages" testId="admin-messages-page">
      <Tabs tabs={TABS.map((t) => [t, t])} active={tab} onChange={(t) => setParams({ tab: t }, { replace: true })} counts={data?.counts} prefix="messages" />
      {!data ? (
        <p className="text-zinc-400">Loading…</p>
      ) : data.items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-700 p-8 text-center text-zinc-400" data-testid="messages-empty">No {tab.toLowerCase()} messages.</p>
      ) : (
        <ul className="space-y-2" data-testid="messages-list">
          {data.items.map((m) => (
            <li key={m.id}>
              <Link to={`/admin/messages/${m.id}`} className="block rounded-lg border border-zinc-800 bg-[#1A1A1E] p-4 transition-colors hover:border-[#FFD23F]/60" data-testid={`message-row-${m.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="min-w-0 break-words text-base font-semibold text-zinc-100" data-testid="message-row-name">{m.name}</p>
                  <p className="shrink-0 text-xs text-zinc-500" data-testid="message-row-received">{shopDateTime(m.created_at)}</p>
                </div>
                <p className="text-sm text-[#FFD23F]" data-testid="message-row-phone">{m.phone}{m.service && <span className="text-zinc-400"> · {m.service}</span>}</p>
                <p className="mt-1 truncate text-sm text-zinc-300" data-testid="message-row-first-line">{m.message.split("\n")[0]}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {m.photo_count > 0 && <span className="inline-flex items-center gap-1 text-xs text-zinc-400" data-testid="message-row-photo-count"><Camera className="h-3.5 w-3.5" /> {m.photo_count} {m.photo_count === 1 ? "photo" : "photos"}</span>}
                  <MessageBadges m={m} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </AdminLayout>
  );
}
